import { mapRecord } from '@deity-io/di-data-basics';
import { cloneDeep } from 'lodash';
import type { IRuntimeState } from './IRuntimeState';
import { isIProducerInstance } from './IRuntimeState';
import { isIExternalInstance } from './tabs/PackagePopoutFlow';

interface IServerStatusEvent {
  uuid: string;
  instanceId?: string | null | undefined;
  instanceType?: string | null | undefined;
  action?: string | null | undefined;
  message: any;
}

interface IServerStatusReportEvent {
  instanceId: string;
  instanceType: string;
  action: 'statusReport';
  message: Record<
    string,
    {
      packageUuid: string;
      packageVersionUuid: string;
      accountUuid: string;
      pipelines: Record<string, any>;
    }
  >;
}

export class RuntimeEventAggregator {
  static add(inputState: IRuntimeState, event: IServerStatusEvent): IRuntimeState | undefined {
    const state = cloneDeep(inputState);
    if (event.action === 'statusReport') {
      const reportEvent = event as IServerStatusReportEvent;
      if (!(reportEvent.instanceType in state.services)) {
        state.services[reportEvent.instanceType] = {};
      }
      if (!(reportEvent.instanceId in state.services[reportEvent.instanceType])) {
        state.services[reportEvent.instanceType][reportEvent.instanceId] = {
          uuid: reportEvent.instanceId
        };
      }

      mapRecord(reportEvent.message, (packageVersion, packageVersionKey) => {
        if (!(packageVersion.packageUuid in state.packages)) {
          state.packages[packageVersion.packageUuid] = {
            uuid: packageVersion.packageUuid,
            accountUuid: packageVersion.accountUuid,
            versionCount: 0,
            pipelineCount: 0,
            count: 0,
            since: Date.now(),
            instances: [],
            versions: {}
          };
        }
        const pack = state.packages[packageVersion.packageUuid];
        if (!(packageVersionKey in state.packages[packageVersion.packageUuid].versions)) {
          state.packages[packageVersion.packageUuid].versions[packageVersionKey] = {
            uuid: packageVersionKey,
            pipelines: {},
            pipelineCount: 0,
            count: 0,
            since: Date.now(),
            instances: []
          };
          pack.versionCount += 1;
        }
        const version = state.packages[packageVersion.packageUuid].versions[packageVersionKey];
        if (!version.instances.find(instance => instance === reportEvent.instanceId)) {
          version.instances.push(reportEvent.instanceId);
        }
        if (!pack.instances.find(instance => instance === reportEvent.instanceId)) {
          pack.instances.push(reportEvent.instanceId);
        }
        mapRecord(packageVersion.pipelines, (pipeline, pipelineKey) => {
          if (!(pipelineKey in version.pipelines)) {
            version.pipelines[pipelineKey] = {
              uuid: pipelineKey,
              count: 0,
              since: Date.now(),
              instances: {}
            };
            version.pipelineCount += 1;
            pack.pipelineCount += 1;
          }
          if (!(reportEvent.instanceId in version.pipelines[pipelineKey].instances)) {
            let count = 0;
            let since = Date.now();
            let last: number | undefined;
            let producerCount = 0;
            if (isIProducerInstance(pipeline)) {
              mapRecord(pipeline.producers, (item, itemKey) => {
                producerCount += 1;
                count += item.count;
                if (since > item.since) {
                  ({ since } = item);
                }

                if (item.last && (!last || last < item.last)) {
                  ({ last } = item);
                }

                return undefined;
              });
            } else if (isIExternalInstance(pipeline)) {
              mapRecord(pipeline.components, (item, itemKey) => {
                producerCount += 1;
                count += item.count;
                if (since > item.since) {
                  ({ since } = item);
                }

                if (item.last && (!last || last < item.last)) {
                  ({ last } = item);
                }

                return undefined;
              });
            } else {
              // console.log('pipeline', pipeline);
            }

            const reportPipeline = version.pipelines[pipelineKey];
            reportPipeline.count += count;
            if (reportPipeline.since > since) {
              reportPipeline.since = since;
            }
            if (last && (!reportPipeline.last || reportPipeline.last < last)) {
              reportPipeline.last = last;
            }

            version.count += count;
            if (version.since > since) {
              version.since = since;
            }
            if (last && (!version.last || version.last < last)) {
              version.last = last;
            }

            pack.count += count;
            if (pack.since > since) {
              pack.since = since;
            }
            if (last && (!pack.last || pack.last < last)) {
              pack.last = last;
            }

            reportPipeline.instances[reportEvent.instanceId] = {
              uuid: reportEvent.instanceId,
              since,
              count,
              last,
              producerCount,
              data: pipeline
            };
          } else {
            // eslint-disable-next-line no-console
            console.warn(
              `Duplicate instance "${reportEvent.instanceId}" in ClusterEventAggregator for pipeline "${pipelineKey}".`
            );
          }
        });
      });

      return state;
    }
  }
}
